import { memo, useEffect, useState, type PointerEvent, type ReactElement } from 'react';
import {
  mapGrid,
  mapStats,
  zoneName,
  zoneRuns,
  type LayoutElementKind,
  type MapResponse,
  type MapSpot,
} from '@kratispot/shared';
import { mapFit } from '@/lib/map-fit';
import { centredOffset, spotSpanIn } from '@/lib/map-view';
import { moneyOf } from '@/lib/format';
import { useTranslation, type CopyKey } from '@/lib/i18n';
import { useViewportWidth } from '@/hooks/use-viewport-width';
import { cn } from '@/lib/utils';
import { MapLegend } from './map-legend';
import { VISITOR_TONE } from './spot-tile';
import { Minimap } from './minimap';
import { SpotTile } from './spot-tile';

const ELEMENT_COPY: Readonly<Record<LayoutElementKind, CopyKey>> = {
  deck: 'deck',
  bar: 'bar',
  kitchen: 'kitchen',
  wall: 'wall',
};

// Decking is drawn as wood, the bar and kitchen as the dark end of the room. §6
const ELEMENT_TONE: Readonly<Record<LayoutElementKind, string>> = {
  deck: 'border border-amber-700/50 bg-[repeating-linear-gradient(180deg,#e2c186_0_4px,#d4af6e_4px_6px)] text-amber-900/70',
  bar: 'bg-stone-800 text-stone-300',
  kitchen: 'bg-stone-700 text-stone-300',
  wall: 'bg-stone-300 text-stone-600',
};

// Memoised: a sweep paints tiles itself, and must not re-render 160 of them. §10
const EMPTY: ReadonlySet<string> = new Set();

export const VenueMap = memo(function VenueMap({
  map,
  selected,
  pulsing,
  focus = EMPTY,
  painting,
  visitor,
  mine,
  onPick,
  onPointerDown,
  onHover,
}: {
  map: MapResponse;
  selected: ReadonlySet<string>;
  pulsing: ReadonlySet<string>;
  // The spots this device last acted on; the first still on the floor is kept
  // in view. Empty for a fresh floor, and for anything another device did.
  focus?: ReadonlySet<string>;
  painting: boolean;
  // The visitor's own two-state floor; the domain never sees this. §12
  visitor?: boolean;
  // The tab's own booking, which nothing on the wire ever told it about. §12
  mine?: ReadonlySet<string>;
  onPick: (spot: MapSpot) => void;
  onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  onHover?: (spot: MapSpot, at: { x: number; y: number } | null) => void;
}): ReactElement {
  const { t, language } = useTranslation();
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  const venueId = map.venue.venueId;

  // The floor opens on its own spots, once per venue — not on every re-read,
  // which would drag the map out from under whoever is reading it. §15
  useEffect(() => {
    if (scroller === null) return;
    const span = spotSpanIn(scroller);
    scroller.scrollLeft = centredOffset(scroller.scrollWidth, scroller.clientWidth, span);
  }, [scroller, venueId]);

  // A spot THIS device just acted on stays in view. Another device's change
  // only pulses; moving the map for it would be somebody else's hand. §16
  useEffect(() => {
    if (scroller === null) return;
    for (const id of focus) {
      const tile = scroller.querySelector(`[data-spot-id="${id}"]`);
      // A bulk action can name a spot a redrawn floor no longer draws. §18
      if (tile === null) continue;
      tile.scrollIntoView({ inline: 'nearest', block: 'nearest' });
      return;
    }
  }, [scroller, focus]);

  const grid = mapGrid(map.venue.layout, map.spots);
  const fit = mapFit(useViewportWidth(), grid.cols);
  const zones = new Map(map.venue.zones.map((zone) => [zone.key, zone]));

  return (
    <section className="overflow-hidden rounded-xl border bg-card shadow-sm">
      {map.venue.layout.frontEdge === 'sea' && (
        <div className="bg-linear-to-b from-sky-300 via-sky-200 to-sky-50 py-2 text-center text-[11px] font-bold tracking-[0.35em] text-sky-900">
          {t('sea')}
        </div>
      )}
      {fit.minimap && <Minimap grid={grid} scroller={scroller} />}
      <div
        ref={setScroller}
        data-testid="map-scroll"
        onPointerDown={onPointerDown}
        data-painting={painting}
        className="overflow-x-auto bg-amber-50/60 p-3"
      >
        <div
          data-testid="map-grid"
          className="grid gap-2"
          style={{
            gridTemplateColumns: `max-content repeat(${grid.cols}, ${fit.tilePx}px)`,
            gridAutoRows: `${fit.tilePx}px`,
            // safe centring: a venue wider than the screen starts at its own edge.
            justifyContent: 'safe center',
          }}
        >
          {!fit.phone &&
            zoneRuns(grid).map((run) => (
              <span
                key={`${run.zoneKey}:${run.gridRow}`}
                className="flex max-w-24 items-center pe-1.5 text-end text-[10px] leading-tight font-bold text-amber-800/70"
                style={{ gridRow: `${run.gridRow} / span ${run.rowSpan}`, gridColumn: 1 }}
                title={zoneName(zones.get(run.zoneKey), language, run.zoneKey)}
              >
                <span className="line-clamp-2">
                  {zoneName(zones.get(run.zoneKey), language, run.zoneKey)}
                </span>
              </span>
            ))}
          {grid.items.map((item) => (
            <span
              key={`${item.kind}:${item.gridRow}:${item.gridCol}`}
              style={{
                gridRow: `${item.gridRow} / span ${item.rowSpan}`,
                gridColumn: `${item.gridCol + 1} / span ${item.colSpan}`,
              }}
              className={cn(item.kind === 'element' && 'grid place-items-center rounded-lg')}
            >
              {item.kind === 'spot' ? (
                <SpotTile
                  spot={item.spot}
                  mode={map.mode}
                  compact={fit.compact}
                  selected={selected.has(item.spot.id)}
                  pulsing={pulsing.has(item.spot.id)}
                  price={
                    visitor === true
                      ? moneyOf(
                          zones.get(item.spot.zoneKey)?.dayPriceCents ?? 0,
                          map.venue.currency,
                          language,
                        )
                      : undefined
                  }
                  mine={mine?.has(item.spot.id)}
                  onPick={onPick}
                  onHover={onHover}
                />
              ) : (
                <span
                  data-testid={`element-${item.element.kind}`}
                  className={cn(
                    'grid h-full w-full place-items-center overflow-hidden rounded-lg text-[9px] font-extrabold tracking-[0.25em] whitespace-nowrap',
                    ELEMENT_TONE[item.element.kind],
                  )}
                >
                  <span className={cn(item.rowSpan > item.colSpan && '[writing-mode:vertical-rl]')}>
                    {t(ELEMENT_COPY[item.element.kind])}
                  </span>
                </span>
              )}
            </span>
          ))}
        </div>
      </div>
      {visitor === true ? (
        <VisitorLegend mine={mine !== undefined && mine.size > 0} />
      ) : (
        <MapLegend stats={mapStats(map.spots, map.mode)} />
      )}
    </section>
  );
});

// Two swatches, no counts, and a third only for a tab holding a booking. §12
function VisitorLegend({ mine }: { mine: boolean }): ReactElement {
  const { t } = useTranslation();
  const states = mine ? (['available', 'unavailable', 'yours'] as const) : VISITOR_STATES;
  return (
    <div
      data-testid="legend"
      className="flex items-center justify-center gap-4 border-t px-3 py-2.5 text-xs font-semibold text-muted-foreground"
    >
      {states.map((state) => (
        <span key={state} className="inline-flex items-center gap-1.5">
          <i aria-hidden className={`size-2.5 rounded-[4px] border ${VISITOR_TONE[state]}`} />
          {t(state === 'yours' ? 'yours' : state)}
        </span>
      ))}
    </div>
  );
}

const VISITOR_STATES = ['available', 'unavailable'] as const;
